import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import {
  LEAVE_TYPES_FORM_EDIT, LEAVE_CATEGORIES,
  PAYROLL_EFFECT_OPTIONS, BALANCE_EFFECT_OPTIONS,
  MONTHLY_GRANT_EFFECT_OPTIONS, CALENDAR_EFFECT_OPTIONS,
} from './constants';
import { inputStyle, outlineBtn, primaryBtn, dangerOutlineBtn } from './stylesB';

const emptyItem = {
  name: '',
  type: '연차',
  category: '유급',
  default_count: '1',
  payroll_effect: 'paid_cover',
  leave_balance_effect: 'deduct',
  monthly_grant_effect: 'neutral',
  calendar_effect: 'full_day',
  sort_order: 100,
};

function rowFromItem(item) {
  return {
    id: item.id || null,
    name: item.name || '',
    type: item.type || '연차',
    category: item.category || '유급',
    default_count: item.default_count != null ? String(Number(item.default_count)) : '',
    payroll_effect: item.payroll_effect || 'none',
    leave_balance_effect: item.leave_balance_effect || 'none',
    monthly_grant_effect: item.monthly_grant_effect || 'neutral',
    calendar_effect: item.calendar_effect || 'none',
    sort_order: item.sort_order ?? 100,
  };
}

export function EventTypeSettingsModal({
  open,
  onClose,
  apiBase,
  eventTypes,
  onChanged,
}) {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows((eventTypes || []).map(rowFromItem));
  }, [open, eventTypes]);

  function updateRow(index, patch) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows((prev) => [...prev, { ...emptyItem, sort_order: (prev.length + 1) * 10 }]);
  }

  async function saveRow(row) {
    setSavingId(row.id || 'new');
    const payload = {
      ...row,
      default_count: row.default_count === '' ? null : Number(row.default_count),
      sort_order: Number(row.sort_order) || 100,
    };
    const result = row.id
      ? await apiClient.put(`${apiBase}/event-types/${row.id}`, payload)
      : await apiClient.post(`${apiBase}/event-types`, payload);
    setSavingId(null);
    if (!result.ok) {
      showToast(result.data?.message || '유형 저장에 실패했습니다.', 'error');
      return;
    }
    showToast('유형 설정을 저장했습니다.', 'success');
    onChanged?.();
  }

  async function removeRow(row, index) {
    if (!row.id) {
      setRows((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm('이 유형 템플릿을 삭제할까요? 이미 사용 중인 유형은 삭제할 수 없습니다.')) return;
    const result = await apiClient.del(`${apiBase}/event-types/${row.id}`);
    if (!result.ok) {
      showToast(result.data?.message || '유형 삭제에 실패했습니다.', 'error');
      return;
    }
    showToast('유형 템플릿을 삭제했습니다.', 'success');
    onChanged?.();
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-sub)' };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 6,
    alignItems: 'end',
  };

  return (
    <Modal
      open={open}
      title="휴가/근태 유형 설정"
      icon="⏰"
      onClose={onClose}
      width="min(1180px, 96vw)"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button style={outlineBtn} onClick={addRow}>+ 유형 추가</button>
          <button style={primaryBtn} onClick={onClose}>닫기</button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-sub)', lineHeight: 1.5 }}>
          이 설정은 앞으로 입력하는 연차/근태 기록의 기본 계산 기준입니다. 이미 저장된 기록은 각 기록에 저장된 스냅샷을 유지합니다.
        </div>

        {rows.map((row, index) => (
          <div key={row.id || `new-${index}`} style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)' }}>
            <div style={gridStyle}>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>이름</span>
                <input style={inputStyle} value={row.name} onChange={(e) => updateRow(index, { name: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>종류</span>
                <select style={inputStyle} value={row.type} onChange={(e) => updateRow(index, { type: e.target.value })}>
                  {LEAVE_TYPES_FORM_EDIT.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>구분</span>
                <select style={inputStyle} value={row.category} onChange={(e) => updateRow(index, { category: e.target.value })}>
                  {LEAVE_CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>개수</span>
                <input style={inputStyle} value={row.default_count} onChange={(e) => updateRow(index, { default_count: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>급여</span>
                <select style={inputStyle} value={row.payroll_effect} onChange={(e) => updateRow(index, { payroll_effect: e.target.value })}>
                  {PAYROLL_EFFECT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>잔여연차</span>
                <select style={inputStyle} value={row.leave_balance_effect} onChange={(e) => updateRow(index, { leave_balance_effect: e.target.value })}>
                  {BALANCE_EFFECT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>월차</span>
                <select style={inputStyle} value={row.monthly_grant_effect} onChange={(e) => updateRow(index, { monthly_grant_effect: e.target.value })}>
                  {MONTHLY_GRANT_EFFECT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={labelStyle}>달력</span>
                <select style={inputStyle} value={row.calendar_effect} onChange={(e) => updateRow(index, { calendar_effect: e.target.value })}>
                  {CALENDAR_EFFECT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={primaryBtn} disabled={savingId === (row.id || 'new')} onClick={() => saveRow(row)}>저장</button>
                <button style={dangerOutlineBtn} onClick={() => removeRow(row, index)}>삭제</button>
              </div>
            </div>
          </div>
        ))}

        {rows.length === 0 && (
          <div style={{ padding: 18, border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--text-sub)', textAlign: 'center' }}>
            등록된 유형 템플릿이 없습니다.
          </div>
        )}
      </div>
    </Modal>
  );
}
